import { zodResolver } from '@hookform/resolvers/zod'
import {
  Clock,
  Globe,
  Hash,
  Layers,
  Map as MapIcon,
  MessageSquare,
  Minus,
  Plus,
  Sparkles,
  Wand2,
  Zap,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { GenerationTimeHint } from '@/features/factory/components/GenerationTimeHint'
import { SuggestPromptButton } from '@/features/factory/components/SuggestPromptButton'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { useStartAtlas, useSuggestPrompt, type AtlasInput } from '@/api/endpoints/factories'
import { useThemes, type Theme } from '@/api/endpoints/themes'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { NarratorPicker } from '../_shared/NarratorPicker'
import { PresetBar } from '../_shared/PresetBar'
import { CinemaBackground } from '../ViralShortsForm/CinemaBackground'
import styles from '../ViralShortsForm/ViralShortsForm.module.scss'
import {
  formToPayload as atlasFormToPayload,
  payloadToForm as atlasPayloadToForm,
  type FormValues as SerializerFormValues,
} from './presetSerializer'

const FIELD_LABELS: Record<string, string> = {
  theme: 'Tema',
  count: 'Quantidade',
  language: 'Idioma',
  custom_prompt: 'Prompt',
  region_scope: 'Região',
  duration_preset: 'Duração por país',
  render_scale: 'Qualidade',
  tone: 'Tom',
  tts_voice_pt: 'Narrador (PT)',
  tts_voice_en: 'Narrador (EN)',
}

const LANGUAGES: ReadonlyArray<SegmentedOption<'pt-br' | 'en' | 'both'>> = [
  { value: 'pt-br', label: 'PT-BR' },
  { value: 'en', label: 'EN' },
  { value: 'both', label: 'Ambos' },
]

type DurationKey = 'short' | 'medium' | 'long'
const DURATIONS: ReadonlyArray<SegmentedOption<DurationKey>> = [
  { value: 'short', label: '6s/país' },
  { value: 'medium', label: '10s/país' },
  { value: 'long', label: '18s/país' },
]

type RegionKey =
  | 'world'
  | 'africa'
  | 'asia'
  | 'europe'
  | 'north_america'
  | 'oceania'
  | 'south_america'
const REGIONS: ReadonlyArray<{ value: RegionKey; label: string }> = [
  { value: 'world', label: 'Mundo todo' },
  { value: 'south_america', label: 'América do Sul' },
  { value: 'north_america', label: 'América do Norte' },
  { value: 'europe', label: 'Europa' },
  { value: 'africa', label: 'África' },
  { value: 'asia', label: 'Ásia' },
  { value: 'oceania', label: 'Oceania' },
]

type ToneKey = 'formal' | 'curious' | 'sarcastic'
const TONES: ReadonlyArray<SegmentedOption<ToneKey>> = [
  { value: 'formal', label: 'Formal' },
  { value: 'curious', label: 'Curioso' },
  { value: 'sarcastic', label: 'Debochado' },
]

type RenderScaleKey = '1' | '2' | '3'
const RENDER_SCALES: ReadonlyArray<SegmentedOption<RenderScaleKey>> = [
  { value: '1', label: 'Rápido' },
  { value: '2', label: 'Padrão' },
  { value: '3', label: 'Máxima' },
]

const META_THEME_OPTIONS = [
  { value: 'random', label: 'Aleatório' },
  { value: 'custom', label: 'Prompt customizado' },
] as const

// custom_prompt intentionally allows empty when theme==='custom' —
// the backend auto-generates a prompt in that case (same behavior as
// clicking "Gerar prompt" manually). If the user types something too
// short we still nudge them to expand it, but blank is a valid submit.
const schema = z.object({
  theme: z.string().min(1, 'Escolha um tema ou prompt'),
  count: z.number().int().min(1).max(10),
  language: z.enum(['pt-br', 'en', 'both']),
  region_scope: z.enum([
    'world',
    'africa',
    'asia',
    'europe',
    'north_america',
    'oceania',
    'south_america',
  ]),
  duration_preset: z.enum(['short', 'medium', 'long']),
  render_scale: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  custom_prompt: z.string().trim().optional(),
  tone: z.enum(['formal', 'curious', 'sarcastic']).optional(),
  tts_voice_pt: z.string().optional(),
  tts_voice_en: z.string().optional(),
  llm_provider: z.enum(['auto', 'cloud', 'local']).optional(),
})

type FormValues = z.infer<typeof schema>

export function AtlasForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  const { data: allThemes = [], isPending: themesPending } = useThemes()
  // Only atlas-* themes apply here. The themes endpoint returns every JSON
  // in config/themes/, so filter client-side by slug prefix.
  const themes = allThemes.filter((t: Theme) => t.slug.startsWith('atlas-'))

  const startMutation = useStartAtlas()
  const suggestPrompt = useSuggestPrompt()

  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      theme: 'random',
      count: 1,
      language: 'pt-br',
      region_scope: 'world',
      duration_preset: 'medium',
      render_scale: 2,
      custom_prompt: '',
      llm_provider: 'auto',
    },
  })

  const theme = form.watch('theme')
  const count = form.watch('count')
  const language = form.watch('language')
  const region = form.watch('region_scope')
  const duration = form.watch('duration_preset')
  const renderScale = form.watch('render_scale')
  const tone = form.watch('tone')
  const voicePt = form.watch('tts_voice_pt')
  const voiceEn = form.watch('tts_voice_en')
  const llmProvider = form.watch('llm_provider')

  const onSubmit = form.handleSubmit((values) => {
    const payload: AtlasInput = {
      theme: values.theme,
      channel: channel || null,
      count: values.count,
      language: values.language,
      region_scope: values.region_scope,
      duration_preset: values.duration_preset,
      render_scale: values.render_scale,
      projection: 'ortho',
      force: false,
      custom_prompt: values.custom_prompt ?? '',
      tone: values.tone ?? null,
      tts_voice_pt: values.tts_voice_pt || null,
      tts_voice_en: values.tts_voice_en || null,
      llm_provider: values.llm_provider ?? 'auto',
    }
    startMutation.mutate(payload, {
      onSuccess: (data) => {
        setActiveJobId(data.job_id)
      },
    })
  })

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset()
            }}
          >
            Gerar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <CinemaBackground />
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <MapIcon aria-hidden /> Atlas · mapas animados
          </div>
          <h1 className={styles.title}>Que parte do mundo vamos mostrar?</h1>
          <p className={styles.subtitle}>
            Escolha um tema; o Prism narra curiosidades sobre países enquanto a câmera voa de um pra outro.
          </p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}
          {channel ? (
            <PresetBar<SerializerFormValues>
              factory="atlas"
              channel={channel}
              formValues={form.watch() as SerializerFormValues}
              formToPayload={atlasFormToPayload}
              payloadToForm={atlasPayloadToForm}
              onApplyPreset={(values) => {
                form.reset(values)
              }}
            />
          ) : null}

          <div className={styles.rows}>
            <Row icon={<Hash />} label="Tema" htmlFor="atlas-theme">
              <SelectRoot
                value={theme}
                onValueChange={(v) => {
                  form.setValue('theme', v, { shouldValidate: true })
                }}
              >
                <SelectTrigger id="atlas-theme" aria-label="Tema">
                  <SelectValue placeholder={themesPending ? 'Carregando...' : 'Escolha um tema'} />
                </SelectTrigger>
                <SelectContent>
                  {META_THEME_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                  {themes.map((t: Theme) => (
                    <SelectItem key={t.slug} value={t.slug}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
              {form.formState.errors.theme ? (
                <span className={styles.errorText}>{form.formState.errors.theme.message}</span>
              ) : null}
            </Row>

            {theme === 'custom' ? (
              <Row icon={<Wand2 />} label="Prompt" htmlFor="atlas-prompt">
                <textarea
                  id="atlas-prompt"
                  className={cn(
                    styles.textarea,
                    form.formState.errors.custom_prompt && styles.textareaError,
                  )}
                  placeholder="Ex: 3 países com nomes que viraram apelidos no Brasil (ou deixe em branco para gerar um automaticamente)"
                  rows={3}
                  {...form.register('custom_prompt')}
                />
                <SuggestPromptButton
                  factory="atlas"
                  language={language}
                  channel={channel}
                  pending={suggestPrompt.isPending}
                  error={suggestPrompt.error}
                  onClick={() => {
                    suggestPrompt.mutate(
                      { factory: 'atlas', language, channel: channel || null },
                      {
                        onSuccess: (prompt) => {
                          form.setValue('custom_prompt', prompt, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        },
                      },
                    )
                  }}
                />
                {form.formState.errors.custom_prompt ? (
                  <span className={styles.errorText}>
                    {form.formState.errors.custom_prompt.message}
                  </span>
                ) : null}
              </Row>
            ) : null}

            <Row icon={<Globe />} label="Região" htmlFor="atlas-region">
              <SelectRoot
                value={region}
                onValueChange={(v) => {
                  form.setValue('region_scope', v as RegionKey)
                }}
              >
                <SelectTrigger id="atlas-region" aria-label="Região">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REGIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </Row>

            <Row icon={<Clock />} label="Duração">
              <Segmented<DurationKey>
                options={DURATIONS}
                value={duration}
                onValueChange={(v) => {
                  form.setValue('duration_preset', v)
                }}
                aria-label="Duração por país"
              />
            </Row>

            <Row icon={<Layers />} label="Quantidade">
              <div className={styles.stepper}>
                <IconButton
                  aria-label="Diminuir"
                  variant="ghost"
                  size="sm"
                  disabled={count <= 1}
                  onClick={() => {
                    form.setValue('count', Math.max(1, count - 1))
                  }}
                >
                  <Minus />
                </IconButton>
                <span className={styles.stepperValue}>{count}</span>
                <IconButton
                  aria-label="Aumentar"
                  variant="ghost"
                  size="sm"
                  disabled={count >= 10}
                  onClick={() => {
                    form.setValue('count', Math.min(10, count + 1))
                  }}
                >
                  <Plus />
                </IconButton>
              </div>
            </Row>

            {advancedOpen ? (
              <div className={styles.advanced}>
                <AdvSection icon={<Globe />} label="Idioma">
                  <Segmented<'pt-br' | 'en' | 'both'>
                    options={LANGUAGES}
                    value={language}
                    onValueChange={(v) => {
                      form.setValue('language', v)
                    }}
                    aria-label="Idioma"
                  />
                </AdvSection>

                <AdvSection icon={<MessageSquare />} label="Tom">
                  <Segmented<ToneKey>
                    options={TONES}
                    value={tone ?? ('' as ToneKey)}
                    onValueChange={(v) => {
                      form.setValue('tone', v)
                    }}
                    aria-label="Tom"
                  />
                </AdvSection>

                <AdvSection icon={<Zap />} label="Qualidade do render">
                  <Segmented<RenderScaleKey>
                    options={RENDER_SCALES}
                    value={String(renderScale) as RenderScaleKey}
                    onValueChange={(v) => {
                      form.setValue('render_scale', Number(v) as 1 | 2 | 3)
                    }}
                    aria-label="Qualidade do render"
                  />
                </AdvSection>

                <NarratorPicker
                  language={language}
                  voicePt={voicePt ?? ''}
                  voiceEn={voiceEn ?? ''}
                  onVoicePtChange={(v) => {
                    form.setValue('tts_voice_pt', v)
                  }}
                  onVoiceEnChange={(v) => {
                    form.setValue('tts_voice_en', v)
                  }}
                />

                <LlmProviderPicker
                  value={llmProvider ?? 'auto'}
                  onChange={(v) => {
                    form.setValue('llm_provider', v)
                  }}
                />
              </div>
            ) : null}
          </div>

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino
            </button>
            <div className={styles.ctaSpacer} />
            <GenerationTimeHint videoKind="atlas" />
            <button
              type="submit"
              className={styles.generateBtn}
              disabled={!channel || startMutation.isPending}
            >
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando...' : 'Gerar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}

interface AdvSectionProps {
  icon: ReactNode
  label: ReactNode
  children: ReactNode
}

function AdvSection({ icon, label, children }: AdvSectionProps) {
  return (
    <section className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          {icon}
        </span>
        {label}
      </div>
      {children}
    </section>
  )
}

